/**
 * An instrument already held in a quantity composition that cannot be held any longer — delisted,
 * in a currency the platform does not support, a matured bond, or flagged delisted with no reason
 * given. The composition response says so per row; the quantity editor parses it onto the row,
 * shows it read-only, locks the position, greys the row and keeps it out of bulk actions, the way
 * the review page treats the same rows. The save is untouched: the row goes out as it came in.
 */
import type { ReviewTicker } from "@eu.mdotm.api/sphere";
import { type RowBlock, rowBlock } from "$root/ui-lib/table/custom-column/instrument/blocking-issue";
import type { Instrument } from "../../const";

/**
 * What blocks a held row, parsed from the response's `issue` and `delisted` at load — the row
 * carries the parse, never the raw pair. A row nothing blocks, and a row added in this edit,
 * carries none of it; outside quantity mode nothing reads it.
 */
export type InstrumentHeldIssueAttributes = {
	heldBlock?: RowBlock;
};

/**
 * `instruments` with what blocks each of them according to `composition`. `instruments` is
 * `createInstrumentList`'s answer for `composition` — one row per entry, in the same order — so
 * the two are read side by side.
 */
export function withHeldIssues(instruments: Instrument[], composition: ReviewTicker[]): Instrument[] {
	return instruments.map((instrument, index) => {
		const ticker = composition[index];
		const heldBlock = ticker ? rowBlock(ticker) : undefined;
		return heldBlock ? { ...instrument, heldBlock } : instrument;
	});
}

/** Whether a held row's instrument cannot be held any longer. */
export function isHeldBlocked(row: Instrument): boolean {
	return row.heldBlock != null;
}

/**
 * What a blocked held row restricts on each quantity column: nothing about the position can be
 * typed. Its values stay on show — they are what the portfolio holds.
 */
export const HELD_BLOCKED_RESTRICTIONS = {
	QUANTITY: { lock: true },
	MARKET_VALUE: { lock: true },
	WEIGHT: { lock: true },
} as const;
